import { Action } from "redux";
import { AuthStatus, TrackingProfile, getCookie, setCookie, syncTrackersToLocalStorage } from "../../lib/accountUtils";
import { m } from '../../lib/selectorUtils';
import { createThunk } from "../../lib/storeUtils";
import { trackingDataSelector } from "../selectors";

export const LOAD_TRACKINGDATA = 'TRACKINGDATA/LOAD';
export const SAVE_TRACKINGDATA = 'TRACKINGDATA/SAVE';


export interface LoadTrackingDataAction extends Action {
  payload: Promise<TrackingProfile>;
}

export interface UpdateTrackingDataAction extends Action {
  payload: TrackingProfile;
}

/**
 * Thunk action to load tracking data from local storage and cookies.
 * This action will dispatch a LOAD_TRACKINGDATA action with the loaded data.
 * The property names are based on API data fields to keep API, cookies and local storage consistent.
 * However, these fields are changed to kebabCase when used in state
 */
export const loadTrackingDataThunk = m(createThunk(
  trackingDataSelector,
  (): LoadTrackingDataAction => ({
    type: LOAD_TRACKINGDATA,
    payload: syncTrackersToLocalStorage()
      .then((trackingData): TrackingProfile => {
        // console.log('[Thunk Action] loadTrackingDataThunk promise resolved with:', { trackingData });
        return trackingData;
      })
      .catch((error) => { 
        console.error('Error loading tracking data:', error);
        return {
          email: undefined,
          given_names: undefined,
          last_name: undefined,
          auth_status: 'impersonated',  // this will denote an error (TODO once impersonation is implemented)
          anonymous_uuid: undefined,
          uuid: undefined,
          last_updated: new Date().toISOString(),
        } as TrackingProfile;
      })
  })
));

/**
 * Thunk action to update tracking data in local storage and cookies.
 * This does not write data to API, that should be done separately.
 * @param trackingData Data to write to tracking cookie and local storage which is merged with existing data.
 * @returns UpdateTrackingDataAction
 */
export const updateTrackingData = (trackingData: TrackingProfile): UpdateTrackingDataAction => {
  const authCookie = getCookie(process.env.COOKIE_NAME || 'id_token');  // Use this to determine if user is authenticated
  const storedCookie = getCookie('sas');
  const parsedCookie = storedCookie ? JSON.parse(storedCookie) : {};
  
  // Get existing mandatory fields from the cookie
  const { anonymous_uuid, is_returning } = parsedCookie;  // auth_status will be determined based on authentication presence
  
  // Get new fields from the incoming data
  const { email, given_names, last_name, roles, postcode, dob, gender, dotDigital, subscriptions } = trackingData;
  const { status } = dotDigital || { status: 'unknown' };  // Default to 'unknown' if dotDigital is not provided

  const payload = {
    // Member fields being updated
    email, 
    given_names, 
    // last_name, 
    // roles, 
    // postcode, 
    // dob, 
    // gender, 
    // subscriptions,
    // dotDigital_status: status,  // dotDigital status
    // Persistent tracking fields
    anonymous_uuid, 
    auth_status: email ? (authCookie ? 'authenticated' : 'unauthenticated') : 'anonymous' as AuthStatus,
    is_returning, 
    // New timestamp
    last_updated: new Date().toISOString() 
  };
  
  console.log('[Action] updateTrackingData called with:', { payload, authCookie });
  setCookie('sas', JSON.stringify(payload));

  return {
    type: SAVE_TRACKINGDATA,
    payload,
  };
};